import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 tabular-nums",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-zinc-950 text-white shadow hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

// StatusBadge conforming to DESIGN.md §13.6
const statusBadgeVariants = cva(
  "inline-flex items-center rounded-full border-transparent font-medium tabular-nums gap-1",
  {
    variants: {
      tone: {
        neutral: "bg-muted text-muted-foreground",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info",
        violet: "bg-violet-soft text-violet",
      },
      size: {
        sm: "px-2 py-0.5 text-micro",
        md: "px-2.5 py-1 text-xs",
      },
    },
    defaultVariants: {
      tone: "neutral",
      size: "sm",
    },
  }
);

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusBadgeVariants> {
  icon?: React.ReactNode;
  status?: "active" | "inactive" | "pending" | "neutral" | "danger" | string;
  label?: string;
}

function StatusBadge({
  tone,
  size,
  icon,
  className,
  children,
  status,
  label,
  ...props
}: StatusBadgeProps) {
  let mappedTone = tone;
  if (!mappedTone && status) {
    if (status === "active" || status === "online") mappedTone = "success";
    else if (status === "pending" || status === "warning") mappedTone = "warning";
    else if (status === "danger" || status === "error") mappedTone = "danger";
    else mappedTone = "neutral";
  }

  return (
    <div
      className={cn(statusBadgeVariants({ tone: mappedTone || "neutral", size }), className)}
      {...props}
    >
      {icon}
      <span>{children || label}</span>
    </div>
  );
}

export { Badge, badgeVariants, StatusBadge, statusBadgeVariants };
